import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  Box,
  ListSubheader,
} from "@mui/material";
import {
  BusinessEntityType,
  useGetBusinessEntitiesQuery,
} from "../../../../graphql/generated";
import { ConditionValueSelectorComponentProps } from "../ConditionForm";
import { castArray } from "lodash";
import { testIds } from "./StorageFacilitySelect.testIds";

const StorageFacilitySelect = ({
  label,
  value,
  onChange,
  customer,
  ...props
}: ConditionValueSelectorComponentProps) => {
  // Fetch receivers associated with the current customer using parentBusinessEntityId filter
  const { data: receiversData, isLoading } = useGetBusinessEntitiesQuery(
    {
      businessEntityTypes: [BusinessEntityType.Receiver],
      filter: customer?._id
        ? { parentBusinessEntityId: customer._id }
        : undefined,
    },
    {
      enabled: !!customer?._id,
    }
  );

  // Group storage facilities by receiver
  const receivers =
    receiversData?.businessEntities.data
      .filter((receiver) => receiver.storageFacilities?.length)
      .map((receiver) => ({
        receiverId: receiver._id,
        receiverName: receiver.name,
        facilities: (receiver.storageFacilities || []).map((facility) => ({
          identifier: facility.identifier,
          // Create composite key: receiverId:storageFacilityId
          compositeKey: `${receiver._id}:${facility.identifier}`,
        })),
      })) || [];

  // Flatten for lookup purposes
  const allFacilities = receivers.flatMap((receiver) =>
    receiver.facilities.map((facility) => ({
      identifier: facility.identifier,
      compositeKey: facility.compositeKey,
      receiverName: receiver.receiverName,
      receiverId: receiver.receiverId,
    }))
  );

  // Handle value being null, undefined, string, or array
  const selectedValues = value
    ? (castArray(value).filter(Boolean) as string[])
    : [];

  return (
    <FormControl fullWidth size="small" {...props}>
      <InputLabel>{label}</InputLabel>
      <Select data-testid={testIds.storageFacilitySelectControl}
        multiple
        value={selectedValues}
        label={label}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        disabled={isLoading}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(selected as string[]).map((compositeKey) => {
              const facility = allFacilities.find(
                (f) => f.compositeKey === compositeKey
              );
              return (
                <Chip
                  key={compositeKey}
                  label={
                    facility
                      ? `${facility.identifier} (${facility.receiverName})`
                      : compositeKey
                  }
                  size="small"
                />
              );
            })}
          </Box>
        )}
      >
        {receivers.map((receiver) => [
          <ListSubheader key={`header-${receiver.receiverId}`}>
            {receiver.receiverName}
          </ListSubheader>,
          ...receiver.facilities.map((facility) => (
            <MenuItem data-testid={`${testIds.storageFacilitySelectFacilityOption}-${facility.compositeKey}`} key={facility.compositeKey} value={facility.compositeKey}>
              {facility.identifier}
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControl>
  );
};

export default StorageFacilitySelect;
